import { bunja } from 'bunja';
import { atom } from 'jotai';

import { inlineSecondaryResetValue } from '@flipster/features/src/shared/tpsl/tpsl-basis-persistence';
import { Order, ordersBunja } from '@flipster/r230522/original/order';
import { Position, positionsBunja } from '@flipster/r230522/original/position';
import { weakR230522SocketBunja } from '@flipster/r230522/original/socket';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { map } from '@flipster/utils/map';
import segment from '@flipster/utils/segment';
import { MarginModeEvent } from '@flipster/utils/segment/events/margin-mode';
import { TradeEvent, TradeEventDataType } from '@flipster/utils/segment/events/trade';
import { OrderMarginType } from '@flipster/utils/types/base';

import { OrderEventContext, OrderEventContextInfo, OrderEventErrorInfo, OrderEventTracker } from '../../tracker';
import { marginTypeBunja, perpAccountBunja, symbolBunja } from './base';
import { formBunja } from './form';
import { validationBunja } from './validation';

export const marginTypeToEventTypeMap: Record<
  OrderMarginType,
  MarginModeEvent['order_form_margin_mode_click']['margin_mode']
> = {
  Isolated: 'Isolated margin',
  Cross: 'Cross margin',
};

export const eventBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { symbol } = bunja.use(symbolBunja);
  const { isOneWayAtom } = bunja.use(perpAccountBunja);
  const { sideAtom, referencePriceAtom, availableFundsAtom, tpSlAtom } = bunja.use(formBunja);
  const { payloadAtom: formSchemaAtom } = bunja.use(validationBunja);
  const { marginTypeAtom } = bunja.use(marginTypeBunja);

  bunja.use(subscribePositionEventBunja);
  bunja.use(subscribeOrderEventBunja);

  const onOrderSubmit = (ctx: OrderEventContext) => {
    const schema = store.get(formSchemaAtom);
    const referencePrice = store.get(referencePriceAtom);
    const marginType = store.get(marginTypeAtom);

    if (schema.side === 'unknown') {
      return;
    }
    const buyCryptoClickParams: TradeEventDataType['buy_crypto_click'] = {
      symbol: ctx.info.symbol,
      side: ctx.info.side,
      order_type: ctx.info.order_type,
      leverage: ctx.info.leverage,
      amount_usdt: ctx.info.amount_usdt,
      amount: ctx.info.amount,
      position_mode: ctx.info.position_mode,
      review_price: Number(schema.triggerPrice || referencePrice),
      trigger_time: schema.triggerTimestamp ?? null,
      is_reduce_only: schema.reduceOnly,
      order_by_token:
        schema.orderState.kind === 'margin'
          ? 'margin'
          : schema.orderState.kind === 'quantity'
            ? 'quantity'
            : 'position_size',
      available_funds: store.get(availableFundsAtom),
      trade_type: 'perpetual',
      margin_mode: marginTypeToEventTypeMap[marginType],
      bbo_price: ctx.info.order_type === 'limit_order' ? schema.limitPrice ?? null : null,
    };
    segment.track(TradeEvent.BuyCryptoClick, buyCryptoClickParams);
  };

  const onOrderSettled = () => {
    store.set(sideAtom, 'unknown');
    store.set(tpSlAtom, (prev) => ({
      ...prev,
      tp: undefined,
      sl: undefined,
      tpSecondary: inlineSecondaryResetValue,
      slSecondary: inlineSecondaryResetValue,
    }));
  };

  const onOrderSuccess = (ctx: OrderEventContext) => {
    onOrderSettled();
    ctx.mark('postApiEndTime');
    ctx.markApiFinish();
  };

  const onOrderFailed = (ctx: OrderEventContext, errorInfo: OrderEventErrorInfo) => {
    ctx.markError(errorInfo);
    onOrderSettled();
    ctx.mark('postApiEndTime');
    ctx.markApiFinish();
  };

  const orderEventContextInfoAtom = atom((get) => {
    const schema = get(formSchemaAtom);
    const isOneWayMode = get(isOneWayAtom);

    const orderType = (() => {
      if (schema.priceType === 'market') return 'market_order';
      if (schema.priceType === 'trigger')
        return schema.triggerType === 'price' ? 'trigger_price_order' : 'trigger_time_order';
      if (schema.priceType === 'limit') return 'limit_order';
    })();

    return {
      cpe_name: 'order_submitted_latency',
      domain: 'trade',
      log_version: 1,
      symbol,
      side: schema.side === 'Long' ? 'buy' : 'sell',
      order_type: orderType!,
      product_type: 'perpetual',
      position_mode: isOneWayMode ? 'oneway' : 'multiposition',
      leverage: schema.orderState.leverage,
      amount_usdt: schema.amount ? Number(schema.amount) : 0,
      amount: schema.orderState.kind === 'quantity' ? Number(schema.orderState.quantity) : null,
    } satisfies OrderEventContextInfo;
  });

  return {
    orderEventContextInfoAtom,
    onOrderSubmit,
    onOrderSettled,
    onOrderSuccess,
    onOrderFailed,
  };
});
eventBunja.debugLabel = 'eventBunja';

const subscribePositionEventBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { r230522Socket } = bunja.use(weakR230522SocketBunja);
  const { positionsAtom } = bunja.use(positionsBunja);

  const matchPosition = (slot: string) => {
    const positions = store.get(positionsAtom);
    return positions?.[slot];
  };

  const addPositionMessage = (position: Position | undefined, slot: string, type: 'insert' | 'update' | 'delete') => {
    const ctx = map(position, (position) => OrderEventTracker.instance.getContext(position.positionId));
    if (!ctx || ctx.type !== type) {
      OrderEventTracker.instance.addPositionMessage((context) => {
        const matchedPosition = position ?? matchPosition(slot);
        if (!matchedPosition) return false;
        return (
          context.info.product_type === 'perpetual' &&
          context.type === type &&
          matchedPosition.positionId === context.requestId
        );
      });
      return;
    }
    ctx.markSocketFinish();
  };

  bunja.effect(() => {
    return r230522Socket.on('private/positions', (event) => {
      const insertEvents = Object.entries(event.data?.i ?? {});
      const updateEventSlots = Object.keys(event.data?.u ?? {});
      const deleteEventSlots = Object.keys(event.data?.d ?? {});

      insertEvents.forEach(([slot, position]) => {
        addPositionMessage(position, slot, 'insert');
      });

      updateEventSlots.forEach((slot) => {
        addPositionMessage(matchPosition(slot), slot, 'update');
      });

      deleteEventSlots.forEach((slot) => {
        addPositionMessage(matchPosition(slot), slot, 'delete');
      });
    });
  });
});
subscribePositionEventBunja.debugLabel = 'subscribePositionEventBunja';

const subscribeOrderEventBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { r230522Socket } = bunja.use(weakR230522SocketBunja);
  const { ordersAtom } = bunja.use(ordersBunja);

  const matchOrder = (slot: string) => {
    const orders = store.get(ordersAtom);
    return orders?.[slot];
  };

  const addOrderMessage = (order: Order | undefined, slot: string, type: 'insert' | 'update' | 'delete') => {
    const ctx = map(order, (order) => OrderEventTracker.instance.getContext(order.orderId));
    if (!ctx || ctx.type === type) {
      OrderEventTracker.instance.addOrderMessage((context) => {
        const matchedOrder = order ?? matchOrder(slot);
        if (!matchedOrder) return false;
        return (
          context.info.product_type === 'perpetual' &&
          context.type === type &&
          matchedOrder.orderId === context.requestId
        );
      });
      return;
    }
    ctx.markSocketFinish();
  };

  bunja.effect(() => {
    return r230522Socket.on('private/orders', (event) => {
      const insertEvents = Object.entries(event.data?.i ?? {});
      const updateEventSlots = Object.keys(event.data?.u ?? {});
      const deleteEventSlots = Object.keys(event.data?.d ?? {});

      insertEvents.forEach(([slot, order]) => {
        addOrderMessage(order, slot, 'insert');
      });

      updateEventSlots.forEach((slot) => {
        addOrderMessage(matchOrder(slot), slot, 'update');
      });

      deleteEventSlots.forEach((slot) => {
        addOrderMessage(matchOrder(slot), slot, 'delete');
      });
    });
  });
});
subscribeOrderEventBunja.debugLabel = 'subscribeOrderEventBunja';
